class LocationInfo extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    this.loadLocation();
  }

  static get observedAttributes() {
    return ["text"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.updateDisplay();
    }
  }

  getTextTemplate() {
    return this.getAttribute("text") || "${CountryCode} ${Emoji}"; // default template
  }

  async loadLocation() {
    // Check if country data is already cached globally
    const countryData = LocationInfo.getCountryData();
    if (countryData) {
      this.countryCode = countryData.countryCode;
      this.countryName = countryData.countryName;
      this.updateDisplay();
      return;
    }

    // Fetch country data if not cached or expired
    try {
      const response = await fetch("/browsing_context_suggestions.json");
      const data = await response.json();

      const countryCode =
        data?.detected_values?.country?.handle?.toUpperCase() || "US";
      const countryName =
        data?.detected_values?.country?.name || "United States";

      // Cache the country data globally
      LocationInfo.setCountryData(countryCode, countryName);

      this.countryCode = countryCode;
      this.countryName = countryName;
      this.updateDisplay();
    } catch (error) {
      console.error("Error fetching location info:", error);
      this.shadowRoot.innerHTML = "📦 Unknown Country";
    }
  }

  // Static methods for managing shared country data cache
  static getCountryData() {
    const cacheKey = "locationCountryData";
    const cached = localStorage.getItem(cacheKey);
    const now = Date.now();
    const THIRTY_MINUTES = 30 * 60 * 1000;

    if (cached) {
      try {
        const { timestamp, countryCode, countryName } = JSON.parse(cached);
        if (now - timestamp < THIRTY_MINUTES) {
          return { countryCode, countryName };
        }
      } catch (e) {
        console.warn("Invalid locationCountryData cache. Will refetch.");
      }
    }
    return null;
  }

  static setCountryData(countryCode, countryName) {
    const cacheKey = "locationCountryData";
    const now = Date.now();

    localStorage.setItem(
      cacheKey,
      JSON.stringify({
        timestamp: now,
        countryCode,
        countryName,
      })
    );
  }

  updateDisplay() {
    if (!this.countryCode) return;

    const flag = this.getFlagEmoji(this.countryCode);
    const template = this.getTextTemplate();

    // Replace placeholders with actual values
    const result = template
      .replace(/\$\{Emoji\}/g, flag)
      .replace(/\$\{CountryCode\}/g, this.countryCode)
      .replace(/\$\{CountryName\}/g, this.countryName)
      .replace(/\$\{Flag\}/g, flag); // Alternative name for emoji

    this.shadowRoot.innerHTML = result;
  }

  getFlagEmoji(countryCode) {
    if (!/^[A-Z]{2}$/.test(countryCode)) {
      return "🏳️"; // Return a white flag for invalid codes
    }
    const codePoints = countryCode
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  }
}

customElements.define("location-info", LocationInfo);
